import React from 'react';
import Markdown from '@components/prompt/chat-thread/response-components/Markdown';
import Feedback from '@components/prompt/chat-thread/FeedbackContainer';
import SummaryLogo from '@icons/prompt/time-line-summary-icon.svg';
import SummaryLoading from '@icons/prompt/prompt-summary-loading.svg';
import { Button, Tooltip } from 'antd';
import copy from 'copy-to-clipboard';
import { ShrinkOutlined, ArrowsAltOutlined, UndoOutlined, CopyOutlined } from '@ant-design/icons';
import {
  AISummaryContainer,
  AISummaryTitle,
  AISummaryContent,
  AISummaryContentMain,
} from './ai-summary-styled';
import { BotMsgOptions } from '@components/prompt/chat-thread/styled';
import { getBotAnswerText, TIMELINE_SUMMARY_QUESTION } from '@components/prompt/utils';
import { summaryCollapse, status, SummaryStatus, copyTextAtom } from '../../atom';
import { useAtomValue, useAtom } from 'jotai';
import { PROMPT_COPY_ANSWER } from '@src/utils/sensor/track';
import apptracker from '@utils/sensor/apptracker';

// TODO add cache
// session id + filter
export default function AISummary({ handleGenerateClick, hasStreamingEnded, botData }) {
  const [copyText, setCopyText] = useAtom(copyTextAtom);
  const summaryState = useAtomValue(status);
  const [collapse, setCollapse] = useAtom(summaryCollapse);

  const text = hasStreamingEnded ? getBotAnswerText(botData) : '';
  const timeStamp = hasStreamingEnded ? new Date() : '';

  const copyResponseToClipboard = () => {
    apptracker(PROMPT_COPY_ANSWER, {
      copyAnswer: TIMELINE_SUMMARY_QUESTION + '\n' + text,
    });
    copy(text);
    setCopyText('Copied to clipboard');
  };

  const handleCollapseClick = () => {
    setCollapse(!collapse);
  };

  return (
    <AISummaryContainer>
      {/* header and logo */}
      <SummaryLogo style={{ width: '16px', height: '16px', verticalAlign: 'middle' }} />
      <AISummaryTitle>AI Summary</AISummaryTitle>
      {summaryState === SummaryStatus.INIT ? (
        <Button
          ghost
          type="primary"
          onClick={handleGenerateClick}
          style={{
            verticalAlign: 'middle',
            marginLeft: '10px',
          }}
        >
          Generate
        </Button>
      ) : null}
      {summaryState === SummaryStatus.GENERATING && botData.length === 0 ? (
        <SummaryLoading
          style={{
            width: '16px',
            height: '16px',
            verticalAlign: 'middle',
            marginLeft: '8px',
          }}
        />
      ) : null}

      {/* summary content */}
      {summaryState !== SummaryStatus.INIT && botData.length ? (
        <AISummaryContent style={{ display: collapse ? 'none' : 'flex' }}>
          <AISummaryContentMain>
            {botData.map((block: { type: string; text: string }, index) => {
              if (block.type === 'markdown') {
                return <Markdown key={index} text={block.text} />;
              }
              return null;
            })}
            {summaryState === SummaryStatus.GOT_LATEST_CONTENT ? (
              <Feedback
                userMsg={TIMELINE_SUMMARY_QUESTION}
                botMsg={text}
                showTime={false}
                timestamp={timeStamp}
                style={{
                  width: 'auto',
                  justifyContent: 'flex-end',
                  position: 'relative',
                  right: '-42px',
                  marginTop: '-8px',
                }}
              />
            ) : null}
          </AISummaryContentMain>
          {summaryState === SummaryStatus.GOT_LATEST_CONTENT ? (
            <BotMsgOptions
              style={{
                margin: '6px 12px 12px',
                position: 'relative',
                right: '-14px',
                height: '25px',
              }}
            >
              <Tooltip title={copyText}>
                <CopyOutlined
                  onClick={copyResponseToClipboard}
                  onMouseLeave={() => setCopyText('Copy Response')}
                  style={{
                    color: '#828282',
                    flexDirection: 'column',
                    fontSize: '14px',
                  }}
                />
              </Tooltip>
            </BotMsgOptions>
          ) : null}
        </AISummaryContent>
      ) : null}

      {/* top right btns */}
      {summaryState === SummaryStatus.GOT_LATEST_CONTENT ? (
        <>
          <Button
            icon={collapse ? <ArrowsAltOutlined /> : <ShrinkOutlined />}
            onClick={handleCollapseClick}
            size="small"
            style={{
              position: 'absolute',
              top: '14px',
              right: '14px',
            }}
          >
            {collapse ? 'Expand' : 'Collapse'}
          </Button>
          <Button
            ghost
            icon={<UndoOutlined />}
            type="primary"
            onClick={handleGenerateClick}
            size="small"
            style={{
              position: 'absolute',
              top: '14px',
              right: '120px',
            }}
          >
            Regenerate
          </Button>
        </>
      ) : null}
    </AISummaryContainer>
  );
}
